import { useState } from "react";
import { Loader2, Save, X, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";

const INPUT_CLS =
  "w-full rounded-xl border border-stone-700/70 bg-ink-900/80 px-4 py-2.5 text-sm text-stone-100 placeholder:text-stone-500 transition-colors focus:border-gold-500/60 focus:outline-none focus:ring-1 focus:ring-gold-500/40";
const LABEL_CLS = "mb-2 block text-[10px] uppercase tracking-[0.28em] text-stone-400";

const emptyForm = {
  title: "", slug: "", category: "", description: "", poster_url: "", banner_url: "", teaser_url: "", trailer_url: "",
  badges: "", status: "upcoming", access: "free", is_launch: false, trending_rank: 0, sort_order: 0,
};

export default function AdminShowForm({ show, onCancel, onSaved }) {
  const [form, setForm] = useState(
    show
      ? { ...show, badges: (show.badges || []).join(", ") }
      : emptyForm
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setChecked = (e) => setForm((f) => ({ ...f, is_launch: e.target.checked }));

  const uploadPoster = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, poster_url: data.url }));
      toast.success("Poster uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Show title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        slug: form.slug || form.title,
        category: form.category,
        description: form.description,
        poster_url: form.poster_url,
        banner_url: form.banner_url,
        teaser_url: form.teaser_url,
        trailer_url: form.trailer_url,
        badges: form.badges.split(",").map((b) => b.trim()).filter(Boolean),
        status: form.status,
        access: form.access,
        is_launch: form.is_launch,
        trending_rank: Number(form.trending_rank) || 0,
        sort_order: Number(form.sort_order) || 0,
      };
      if (show) {
        await api.put(`/shows/${show.id}`, payload);
        toast.success("Show updated");
      } else {
        await api.post("/shows", payload);
        toast.success("Show added");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="admin-show-form" onSubmit={submit} className="rounded-2xl border border-gold-500/25 bg-ink-900/60 p-6 sm:p-8">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="font-display text-lg font-bold uppercase tracking-[0.2em] text-gold-300">
          {show ? `Edit — ${show.title}` : "Add New Show"}
        </h3>
        <button type="button" data-testid="admin-show-form-cancel-button" onClick={onCancel} aria-label="Cancel" className="rounded-full border border-stone-700 p-2 text-stone-400 hover:border-gold-500/50 hover:text-gold-400">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={LABEL_CLS}>Title *</label>
          <input data-testid="admin-show-form-title-input" required value={form.title} onChange={set("title")} placeholder="Show title" className={INPUT_CLS} />
        </div>
        <div>
          <label className={LABEL_CLS}>Slug</label>
          <input data-testid="admin-show-form-slug-input" value={form.slug} onChange={set("slug")} placeholder="auto from title" className={INPUT_CLS} />
        </div>
        <div>
          <label className={LABEL_CLS}>Category</label>
          <input data-testid="admin-show-form-category-input" value={form.category} onChange={set("category")} placeholder="e.g. Drama · Series" className={INPUT_CLS} />
        </div>
        <div>
          <label className={LABEL_CLS}>Badges (comma separated)</label>
          <input data-testid="admin-show-form-badges-input" value={form.badges} onChange={set("badges")} placeholder="Trending, Exclusive, Coming Soon" className={INPUT_CLS} />
        </div>
        <div>
          <label className={LABEL_CLS}>Status</label>
          <select data-testid="admin-show-form-status-select" value={form.status} onChange={set("status")} className={INPUT_CLS}>
            <option value="live">Live</option>
            <option value="upcoming">Upcoming</option>
          </select>
        </div>
        <div>
          <label className={LABEL_CLS}>Access Tier</label>
          <select data-testid="admin-show-form-access-select" value={form.access} onChange={set("access")} className={INPUT_CLS}>
            <option value="free">Free (with ads)</option>
            <option value="premium">Premium (₹199/mo)</option>
          </select>
        </div>
        <div>
          <label className={LABEL_CLS}>Teaser URL (YouTube / MP4)</label>
          <input data-testid="admin-show-form-teaser-url-input" value={form.teaser_url} onChange={set("teaser_url")} placeholder="https://youtube.com/watch?v=... or .mp4 URL" className={INPUT_CLS} />
        </div>
        <div>
          <label className={LABEL_CLS}>Trailer Link (YouTube / MP4)</label>
          <input data-testid="admin-show-form-trailer-url-input" value={form.trailer_url} onChange={set("trailer_url")} placeholder="https://youtube.com/watch?v=... or .mp4 URL" className={INPUT_CLS} />
        </div>
        <div>
          <label className={LABEL_CLS}>Trending Rank</label>
          <input data-testid="admin-show-form-trending-rank-input" type="number" min="0" value={form.trending_rank} onChange={set("trending_rank")} className={INPUT_CLS} />
        </div>
        <div>
          <label className={LABEL_CLS}>Sort Order</label>
          <input data-testid="admin-show-form-sort-order-input" type="number" min="0" value={form.sort_order} onChange={set("sort_order")} className={INPUT_CLS} />
        </div>
        <div>
          <label className={LABEL_CLS}>Banner Image URL</label>
          <input data-testid="admin-show-form-banner-url-input" value={form.banner_url} onChange={set("banner_url")} placeholder="Wide banner image URL (optional)" className={INPUT_CLS} />
        </div>
        <div className="sm:col-span-2">
          <label className={LABEL_CLS}>Description</label>
          <textarea data-testid="admin-show-form-description-input" rows={3} value={form.description} onChange={set("description")} placeholder="Short synopsis" className={`${INPUT_CLS} resize-none`} />
        </div>

        <div className="sm:col-span-2">
          <label className={LABEL_CLS}>Poster</label>
          <div className="flex flex-col gap-4 rounded-2xl border border-stone-800 bg-ink-950/60 p-4 sm:flex-row sm:items-center">
            <div className="flex h-32 w-22 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-stone-800 bg-ink-900" data-testid="admin-show-form-poster-preview">
              {form.poster_url ? (
                <img src={fileUrl(form.poster_url)} alt="Poster preview" className="h-full w-full object-cover" />
              ) : (
                <ImageIcon className="h-8 w-8 text-stone-700" />
              )}
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                  data-testid="admin-show-form-poster-upload-input"
                  onChange={uploadPoster}
                  className="hidden"
                  id="poster-upload"
                />
                <label
                  htmlFor="poster-upload"
                  data-testid="admin-show-form-poster-upload-button"
                  className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-gold-500/50 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-gold-300 transition-colors hover:bg-gold-500/10"
                >
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  {uploading ? "Uploading..." : "Upload Poster"}
                </label>
              </div>
              <input data-testid="admin-show-form-poster-url-input" value={form.poster_url} onChange={set("poster_url")} placeholder="or paste a poster URL / asset path" className={INPUT_CLS} />
            </div>
          </div>
        </div>

        <label className="flex items-center gap-3 text-sm text-stone-300 sm:col-span-2">
          <input type="checkbox" data-testid="admin-show-form-is-launch-checkbox" checked={form.is_launch} onChange={setChecked} className="h-4 w-4 accent-[#d4af37]" />
          Official Launch show (features in the hero spotlight)
        </label>
      </div>

      <button
        type="submit"
        data-testid="admin-show-form-save-button"
        disabled={saving || uploading}
        className="mt-7 inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-gold-600 via-gold-400 to-gold-600 px-8 py-3 text-[12px] font-bold uppercase tracking-[0.2em] text-ink-950 transition-all duration-300 hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        {show ? "Save Changes" : "Add Show"}
      </button>
    </form>
  );
}
